import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// ── Ambient depth orb ─────────────────────────────────────────────
interface OrbProps { x: string; y: string; size: number; blur: number; opacity: number; }
const Orb: React.FC<OrbProps> = ({ x, y, size, blur, opacity }) => (
  <div style={{
    position: 'absolute', left: x, top: y,
    width: size, height: size, borderRadius: '50%',
    background: 'radial-gradient(circle, #3A6040 0%, transparent 70%)',
    filter: `blur(${blur}px)`,
    opacity,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  }} />
);

// ── Leaf SVG ──────────────────────────────────────────────────────
const Leaf: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg viewBox="0 0 40 62" style={{ pointerEvents: 'none', ...style }}>
    <path d="M20 58 C20 58 4 36 7 17 C10 3 20 1 20 1 C20 1 30 3 33 17 C36 36 20 58 20 58Z"
          fill="currentColor" opacity="0.45" />
    <line x1="20" y1="1" x2="20" y2="58" stroke="currentColor" strokeWidth="0.7" opacity="0.25" />
    <path d="M20 20 Q12 26 9 34" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.2" />
    <path d="M20 20 Q28 26 31 34" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.2" />
  </svg>
);

// ── Section wave divider ──────────────────────────────────────────
interface DividerProps { from: string; to: string; invert?: boolean; }
const WaveDivider: React.FC<DividerProps> = ({ from, to, invert = false }) => (
  <div style={{ background: from, lineHeight: 0, position: 'relative', zIndex: 2 }}>
    <svg
      viewBox="0 0 1440 72"
      preserveAspectRatio="none"
      style={{ display: 'block', width: '100%', height: 72, transform: invert ? 'scaleX(-1)' : undefined }}
    >
      {/* Fill shape */}
      <path d="M0,36 C200,4 400,68 720,36 S1200,4 1440,36 L1440,72 L0,72Z" fill={to} />
      {/* Botanical accent stroke along the wave */}
      <path d="M0,36 C200,4 400,68 720,36 S1200,4 1440,36"
            stroke="#7A8C5E" strokeWidth="1.2" fill="none" opacity="0.3" />
      {/* Small leaf accents on the wave */}
      <ellipse cx="360" cy="22" rx="5" ry="8" fill="#7A8C5E" opacity="0.2" transform="rotate(-30 360 22)" />
      <ellipse cx="720" cy="36" rx="4" ry="7" fill="#7A8C5E" opacity="0.2" transform="rotate(10 720 36)" />
      <ellipse cx="1080" cy="22" rx="5" ry="8" fill="#7A8C5E" opacity="0.2" transform="rotate(25 1080 22)" />
    </svg>
  </div>
);

// ── Letter-stagger title ──────────────────────────────────────────
interface StaggerWordProps {
  word: string;
  letterStyle: React.CSSProperties;
  baseDelay?: number;
}
const StaggerWord: React.FC<StaggerWordProps> = ({ word, letterStyle, baseDelay = 0 }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline' }}>
    {word.split('').map((char, i) => (
      <div key={i} style={{ overflow: 'hidden', display: 'inline-block' }}>
        <motion.span
          initial={{ y: '105%', opacity: 0 }}
          animate={{ y: '0%', opacity: 1 }}
          transition={{ duration: 0.7, delay: baseDelay + i * 0.055, ease: [0.22, 1, 0.36, 1] }}
          style={{ display: 'inline-block', ...letterStyle }}
        >
          {char}
        </motion.span>
      </div>
    ))}
  </div>
);

// ── Products ──────────────────────────────────────────────────────
const products = [
  { name: "Kava Tincture", price: "$24.00", description: "This makes you mellow as heck.", tag: "Chill" },
  { name: "Pine & Cedar Bath Salts", price: "$18.00", description: "Mineral-rich soak with Ozark pine resin and cedarwood oil.", tag: "Relax" },
  { name: "Lemon Balm Dream Tea", price: "$14.00", description: "Calming evening blend of lemon balm, chamomile, and valerian.", tag: "Sleep" },
  { name: "Beard Salve for Yo Face", price: "$16.00", description: "Calendula and comfrey infused in local beeswax for skin repair.", tag: "Heal" },
  { name: "Wildcrafted Oil Blend", price: "$28.00", description: "Foraged botanicals in jojoba — for morning ritual or evening wind-down.", tag: "Ritual" },
  { name: "Ozark Roots Bundle", price: "$52.00", description: "Best-selling trio: tincture, tea, and salve in a gift box.", tag: "Bundle" },
];

// ── Page ──────────────────────────────────────────────────────────
const ApothecaryPage: React.FC = () => {
  const navigate = useNavigate();

  // Hero scroll — 3 depth layers at different speeds
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY     = useTransform(heroScroll, [0, 1], ['0%', '55%']);   // deep bg — slowest
  const orbY    = useTransform(heroScroll, [0, 1], ['0%', '32%']);   // mid orbs
  const leafY   = useTransform(heroScroll, [0, 1], ['0%', '15%']);   // near botanicals
  const heroOpacity = useTransform(heroScroll, [0, 0.72], [1, 0]);

  // Mouse parallax on hero text
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 55, damping: 16 });
  const springY = useSpring(mouseY, { stiffness: 55, damping: 16 });
  const titleX = useTransform(springX, [-1, 1], [-12, 12]);
  const titleY = useTransform(springY, [-1, 1], [-7, 7]);

  const onMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - r.left) / r.width * 2 - 1);
    mouseY.set((e.clientY - r.top)  / r.height * 2 - 1);
  };

  // About scroll parallax — text and image at different rates
  const aboutRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: aboutScroll } = useScroll({ target: aboutRef, offset: ['start end', 'end start'] });
  const aboutTextY = useTransform(aboutScroll, [0, 1], ['6%', '-6%']);
  const aboutImgY  = useTransform(aboutScroll, [0, 1], ['10%', '-10%']);

  return (
    <div style={{ background: '#1A2E1A', fontFamily: "'Georgia', serif", overflowX: 'hidden' }}>
      {/* Fixed back button */}
      <button
        onClick={() => navigate('/life')}
        style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          background: 'rgba(242,232,204,0.08)',
          border: '1px solid rgba(242,232,204,0.18)',
          color: 'rgba(242,232,204,0.55)',
          padding: '5px 12px',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 10, fontWeight: 500,
          cursor: 'pointer', borderRadius: 3,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}
      >
        ← Back
      </button>

      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <section
        ref={heroRef}
        onMouseMove={onMouseMove}
        className="relative h-screen overflow-hidden flex items-center justify-center"
      >
        {/* Depth layer 1 — deep background gradient (slowest) */}
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at 50% 65%, #2D5234 0%, #1A2E1A 48%, #090F09 100%)',
          }} />
          {/* Grain texture */}
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            opacity: 0.028,
          }} />
        </motion.div>

        {/* Depth layer 2 — ambient orbs (medium) */}
        <motion.div style={{ y: orbY }} className="absolute inset-0 pointer-events-none">
          <Orb x="18%" y="28%" size={520} blur={140} opacity={0.38} />
          <Orb x="82%" y="62%" size={400} blur={120} opacity={0.28} />
          <Orb x="48%" y="82%" size={300} blur={100} opacity={0.22} />
          <Orb x="88%" y="22%" size={220} blur={80}  opacity={0.18} />
        </motion.div>

        {/* Depth layer 3 — botanical leaf elements (fastest / nearest) */}
        <motion.div style={{ y: leafY }} className="absolute inset-0 pointer-events-none">
          <Leaf style={{ position: 'absolute', left: '6%',  top: '12%',  width: 44, height: 66, color: '#7A8C5E', transform: 'rotate(-28deg)' }} />
          <Leaf style={{ position: 'absolute', left: '9%',  top: '19%',  width: 30, height: 45, color: '#9AAB7A', transform: 'rotate(-12deg)' }} />
          <Leaf style={{ position: 'absolute', left: '4%',  bottom: '22%', width: 26, height: 39, color: '#6B7C50', transform: 'rotate(-42deg)' }} />
          <Leaf style={{ position: 'absolute', right: '6%', top: '18%',  width: 40, height: 60, color: '#7A8C5E', transform: 'rotate(24deg) scaleX(-1)' }} />
          <Leaf style={{ position: 'absolute', right: '9%', bottom: '28%', width: 32, height: 48, color: '#9AAB7A', transform: 'rotate(38deg) scaleX(-1)' }} />
          <Leaf style={{ position: 'absolute', right: '3%', top: '48%',  width: 22, height: 33, color: '#6B7C50', transform: 'rotate(55deg) scaleX(-1)' }} />
          {/* Scattered dots at depth */}
          {[
            { x: '16%', y: '54%', s: 5 }, { x: '83%', y: '40%', s: 3 },
            { x: '27%', y: '74%', s: 4 }, { x: '71%', y: '16%', s: 3 },
            { x: '91%', y: '68%', s: 5 }, { x: '38%', y: '11%', s: 3 },
            { x: '55%', y: '88%', s: 4 }, { x: '13%', y: '38%', s: 3 },
          ].map((d, i) => (
            <div key={i} style={{
              position: 'absolute', left: d.x, top: d.y,
              width: d.s, height: d.s, borderRadius: '50%',
              background: '#7A8C5E', opacity: 0.35,
              transform: 'translate(-50%,-50%)',
            }} />
          ))}
        </motion.div>

        {/* Hero text — mouse-tracked parallax */}
        <motion.div
          style={{ opacity: heroOpacity, x: titleX, y: titleY }}
          className="relative z-10 text-center px-6 select-none"
        >
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center justify-center gap-4 mb-7"
          >
            <div className="h-px w-10" style={{ background: '#7A8C5E' }} />
            <span className="text-xs tracking-[0.42em] uppercase" style={{ color: '#7A8C5E' }}>Est. 2026</span>
            <div className="h-px w-10" style={{ background: '#7A8C5E' }} />
          </motion.div>

          <StaggerWord
            word="OZARK"
            baseDelay={0.22}
            letterStyle={{ color: '#F2E8CC', fontWeight: 700, fontSize: 'clamp(4.5rem, 11vw, 10rem)', letterSpacing: '-0.02em', lineHeight: 1 }}
          />

          <motion.p
            initial={{ opacity: 0, letterSpacing: '0.7em' }}
            animate={{ opacity: 1, letterSpacing: '0.38em' }}
            transition={{ duration: 1.1, delay: 0.75 }}
            style={{ color: '#9AAB7A', fontSize: 'clamp(1rem, 2.2vw, 1.5rem)', textTransform: 'uppercase', margin: '4px 0' }}
          >
            Urban
          </motion.p>

          <StaggerWord
            word="APOTHECARY"
            baseDelay={0.58}
            letterStyle={{ color: '#F2E8CC', fontWeight: 700, fontSize: 'clamp(1.9rem, 4.5vw, 5rem)', letterSpacing: '0.07em', lineHeight: 1 }}
          />

          <motion.div
            initial={{ opacity: 0, scaleX: 0.3 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center justify-center gap-4"
            style={{ margin: '28px 0 32px', transformOrigin: 'center' }}
          >
            <div className="h-px w-14" style={{ background: '#7A8C5E' }} />
            <p style={{ color: '#9AAB7A', fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              Rooted in the Ozarks&nbsp;•&nbsp;Crafted in the City
            </p>
            <div className="h-px w-14" style={{ background: '#7A8C5E' }} />
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 1.35 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            style={{
              border: '1px solid #F2E8CC', color: '#F2E8CC', background: 'transparent',
              padding: '12px 36px', fontSize: 11,
              letterSpacing: '0.22em', textTransform: 'uppercase', cursor: 'pointer',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(242,232,204,0.1)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            Shop Now
          </motion.button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 1.9 }}
          style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
        >
          <span style={{ color: '#7A8C5E', fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Scroll</span>
          <motion.div
            animate={{ y: [0, 9, 0] }}
            transition={{ repeat: Infinity, duration: 1.7 }}
            style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, #7A8C5E, transparent)' }}
          />
        </motion.div>
      </section>

      {/* ── Wave: dark → parchment ── */}
      <WaveDivider from="#090F09" to="#F2E8CC" />

      {/* ═══════════════════════ ABOUT ═══════════════════════ */}
      <section ref={aboutRef} className="relative overflow-hidden" style={{ background: '#F2E8CC', paddingTop: '4rem', paddingBottom: '6rem' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div style={{ y: aboutTextY }}>
              <p style={{ color: '#7A8C5E', fontSize: 11, letterSpacing: '0.4em', textTransform: 'uppercase', marginBottom: 16, fontFamily: 'Inter, sans-serif' }}>
                Our Story
              </p>
              <h2 style={{ color: '#3D2410', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 700, lineHeight: 1.15, marginBottom: 24 }}>
                Wild roots.<br />City hands.
              </h2>
              <div style={{ height: 1, width: 48, background: '#7A8C5E', marginBottom: 24 }} />
              <p style={{ color: '#5C3D20', lineHeight: 1.75, marginBottom: 16, fontSize: '0.95rem' }}>
                We forage, grow, and source botanicals from the Ozark highlands — then bring them into our urban workshop to craft small-batch remedies, teas, and body goods rooted in generations of Appalachian herbalism.
              </p>
              <p style={{ color: '#5C3D20', lineHeight: 1.75, fontSize: '0.95rem' }}>
                Every product carries the smell of cedar, the patience of slow extraction, and the care of someone who believes plants have more to offer than we've remembered.
              </p>

              {/* Brand pillars */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 32 }}>
                {[['Wild', 'Harvested'], ['Small', 'Batch'], ['Ozark', 'Grown']].map(([a, b]) => (
                  <div key={a} style={{ textAlign: 'center' }}>
                    <div style={{ height: 1, background: '#C4A96A', marginBottom: 12 }} />
                    <p style={{ color: '#3D2410', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>{a}</p>
                    <p style={{ color: '#7A8C5E', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>{b}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Image — drop about.jpg into /public/assets/media/examples/apothecary/ */}
            <motion.div style={{ y: aboutImgY, position: 'relative' }}>
              <div style={{
                aspectRatio: '4/5', overflow: 'hidden', position: 'relative',
                background: 'linear-gradient(160deg, #2B4A2F, #0D1A0D)',
              }}>
                <img
                  src="/assets/media/examples/apothecary/about.jpg"
                  alt="Workshop"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.88, display: 'block' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                {/* Mortar placeholder (visible until image loads) */}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg viewBox="0 0 120 120" style={{ width: 96, height: 96, opacity: 0.18 }}>
                    <ellipse cx="60" cy="92" rx="34" ry="11" fill="#F2E8CC" />
                    <path d="M28 92 Q24 62 34 46 Q44 30 60 28 Q76 30 86 46 Q96 62 92 92Z" fill="#F2E8CC" />
                    <path d="M57 28 L61 10 L65 8 L62 26" stroke="#F2E8CC" strokeWidth="3" fill="none" />
                    <ellipse cx="62" cy="8" rx="6" ry="4" fill="#F2E8CC" />
                  </svg>
                </div>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(13,26,13,0.35) 0%, transparent 50%)' }} />
              </div>
              {/* Corner tag */}
              <div style={{
                position: 'absolute', top: 16, left: 16,
                background: 'rgba(242,232,204,0.92)', border: '1px solid #7A8C5E',
                padding: '4px 12px', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase',
                color: '#3D2410', fontFamily: 'Inter, sans-serif',
              }}>
                Small Batch
              </div>
              {/* Offset accent border */}
              <div style={{
                position: 'absolute', bottom: -16, right: -16,
                width: '100%', height: '100%', zIndex: -1,
                border: '1px solid #C4A96A',
              }} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Wave: parchment → darkest green ── */}
      <WaveDivider from="#F2E8CC" to="#090F09" invert />

      {/* ═══════════════════════ SHOP ═══════════════════════ */}
      <section style={{ background: '#090F09', paddingTop: '5rem', paddingBottom: '6rem', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: 56 }}
          >
            <p style={{ color: '#7A8C5E', fontSize: 10, letterSpacing: '0.42em', textTransform: 'uppercase', marginBottom: 14, fontFamily: 'Inter, sans-serif' }}>
              Small Batch Goods
            </p>
            <h2 style={{ color: '#F2E8CC', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 700 }}>The Shop</h2>
            <div style={{ height: 1, width: 48, background: '#7A8C5E', margin: '20px auto 0' }} />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((product, i) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                whileHover={{ y: -7, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
                style={{ border: '1px solid #2B4A2F', background: '#0D1A0D', cursor: 'pointer' }}
              >
                {/* Product image slot — drop files in /public/assets/media/examples/apothecary/products/ */}
                <div style={{
                  height: 176, position: 'relative', overflow: 'hidden',
                  borderBottom: '1px solid #2B4A2F',
                  background: 'linear-gradient(135deg, #1A2E1A 0%, #2B4A2F 100%)',
                }}>
                  <img
                    src={`/assets/media/examples/apothecary/products/${product.name.toLowerCase().replace(/\s+/g, '-')}.jpg`}
                    alt={product.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.82, display: 'block' }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 55%)' }} />
                  <span style={{
                    position: 'absolute', top: 12, left: 12,
                    border: '1px solid #7A8C5E', color: '#9AAB7A',
                    padding: '3px 10px', fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
                    background: 'rgba(13,26,13,0.75)', fontFamily: 'Inter, sans-serif',
                  }}>
                    {product.tag}
                  </span>
                </div>

                <div style={{ padding: 20 }}>
                  <h3 style={{ color: '#F2E8CC', fontWeight: 700, fontSize: '0.875rem', letterSpacing: '0.04em', marginBottom: 6 }}>
                    {product.name}
                  </h3>
                  <p style={{ color: '#8A9A7A', fontSize: '0.78rem', lineHeight: 1.6, marginBottom: 16 }}>
                    {product.description}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#C4A96A', fontWeight: 700, fontSize: '0.95rem' }}>{product.price}</span>
                    <motion.button
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.96 }}
                      style={{
                        border: '1px solid #7A8C5E', color: '#9AAB7A', background: 'transparent',
                        padding: '7px 16px', fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
                        cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#2B4A2F'; e.currentTarget.style.color = '#F2E8CC'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9AAB7A'; }}
                    >
                      Add to Cart
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Wave: darkest green → parchment ── */}
      <WaveDivider from="#090F09" to="#F2E8CC" />

      {/* ═══════════════════════ CTA ═══════════════════════ */}
      <section style={{ background: '#F2E8CC', paddingTop: '5rem', paddingBottom: '7rem', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
        <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p style={{ color: '#7A8C5E', fontSize: 10, letterSpacing: '0.42em', textTransform: 'uppercase', marginBottom: 16, fontFamily: 'Inter, sans-serif' }}>
              Stay Rooted
            </p>
            <h2 style={{ color: '#3D2410', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: 16 }}>
              Join the harvest.
            </h2>
            <p style={{ color: '#5C3D20', lineHeight: 1.75, fontSize: '0.95rem', marginBottom: 32 }}>
              New batches drop seasonally. Get notified when we restock — and be first to know about market days, workshops, and limited editions.
            </p>

            <div style={{ display: 'flex', maxWidth: 360, margin: '0 auto' }}>
              <input
                type="email"
                placeholder="your@email.com"
                style={{
                  flex: 1, padding: '12px 16px', fontSize: '0.875rem',
                  border: '1px solid #3D2410', borderRight: 'none', outline: 'none',
                  background: 'transparent', color: '#3D2410', fontFamily: 'Georgia, serif',
                }}
              />
              <button
                style={{
                  padding: '12px 24px', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase',
                  background: '#3D2410', color: '#F2E8CC', border: 'none', cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#2B4A2F')}
                onMouseLeave={e => (e.currentTarget.style.background = '#3D2410')}
              >
                Join
              </button>
            </div>

            <div style={{ marginTop: 64, paddingTop: 32, borderTop: '1px solid rgba(61,36,16,0.2)' }}>
              <p style={{ color: '#9A7A5A', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>
                Ozark Urban Apothecary&nbsp;•&nbsp;Est. 2026
              </p>
              <p style={{ color: '#B4926A', fontSize: 11, marginTop: 4, fontFamily: 'Georgia, serif' }}>
                Rooted in the Ozarks&nbsp;•&nbsp;Crafted in the City
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ApothecaryPage;
